import { Injectable } from '@angular/core';
import { IClusterService } from '../IClusterService';
import { Cluster, ClusterKey } from '@kw-shared/common';
import { ApiDataService } from '@kw-shared/api-data-access';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RemoteClusterService implements IClusterService {
  constructor(private readonly apiDataService: ApiDataService) {}

  public async readClustersEnabled(wallboxId: string): Promise<boolean> {
    let settings = await firstValueFrom(this.apiDataService.wallboxes.getWallboxSettings(wallboxId));
    if (settings.configuration.hasOwnProperty(ClusterKey.CLUSTERS_ENABLE)) {
      return settings.configuration[ClusterKey.CLUSTERS_ENABLE] === 'true';
    }

    return false;
  }

  public async writeClustersEnabled(enabled: boolean, wallboxId: string): Promise<void> {
    await firstValueFrom(
      this.apiDataService.wallboxes.saveWallboxSettings(wallboxId, {
        configuration: { [ClusterKey.CLUSTERS_ENABLE]: enabled.toString() },
      })
    );
  }

  public async readClusters(wallboxId: string): Promise<{ clusters: Cluster[] }> {
    try {
      let data = await firstValueFrom(this.apiDataService.clusters.readClusters(wallboxId));
      return { clusters: data };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public createCluster(cluster: Cluster, otherClusters: Cluster[], wallboxId: string): Promise<void> {
    let clusters = [...otherClusters, cluster];
    let remoteClusters = this.convertToRemoteClusters(clusters);
    return this.apiDataService.clusters.setClusters(wallboxId, remoteClusters);
  }

  public updateCluster(cluster: Cluster, otherClusters: Cluster[], wallboxId: string): Promise<void> {
    let relevantClusterIndex = otherClusters.findIndex(it => it.number === cluster.number);
    if (relevantClusterIndex >= 0) {
      otherClusters[relevantClusterIndex] = cluster;
    }
    let remoteClusters = this.convertToRemoteClusters(otherClusters);
    return this.apiDataService.clusters.setClusters(wallboxId, remoteClusters);
  }

  public deleteCluster(cluster: Cluster, otherClusters: Cluster[], wallboxId: string): Promise<void> {
    let relevantClusterIndex = otherClusters.findIndex(it => it.number === cluster.number);
    if (relevantClusterIndex >= 0) {
      otherClusters.splice(relevantClusterIndex, 1);
    }
    return this.setClusters(otherClusters, wallboxId);
  }

  public setClusters(clusters: Cluster[], wallboxId: string): Promise<void> {
    let remoteClusters = this.convertToRemoteClusters(clusters);
    try {
      void this.apiDataService.clusters.setClusters(wallboxId, remoteClusters);
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  private convertToRemoteClusters(otherClusters: Cluster[]) {
    return otherClusters.map((cluster: Cluster) => ({
      id: '',
      number: cluster.number,
      alias: cluster.alias,
      maxCurrent: cluster.maxCurrent,
      minCurrent: cluster.minCurrent,
      phaseAssignment: cluster.phaseAssignment,
    }));
  }
}
